import { Button, Spinner, Tooltip } from '@ibsys/kit';
import { DownloadIcon } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { DashboardPreset } from '@/entities/dashboards';
import { EditableTitle, SaveButton } from '@/shared/ui';
import { DashboardPresetActions } from './DashboardPresets/DashboardPresetActions';
import { DashboardPresetsPopover } from './DashboardPresets/DashboardPresetsPopover';

type Props = {
  /** Название применённого дашборда; `null` — показан стандартный дашборд */
  presetName: string | null;
  /** Идёт правка дашборда: его название правится в заголовке, а управление дашбордами скрыто */
  isEditing: boolean;
  /** Название правящегося дашборда */
  draftName: string;
  onChangeDraftName: (name: string) => void;
  /** В составе правящегося дашборда есть правки, не записанные в пресет */
  isDirty: boolean;
  /** Состав годится к записи: у дашборда есть название и хотя бы одна диаграмма */
  canSave: boolean;
  onSaveDraft: () => void;
  onCancelEditing: () => void;
  /** Пользовательские дашборды доступны — показаны список и действия над применённым */
  isPresetsEnabled: boolean;
  /** Данные дашборда обновляются */
  isFetching: boolean;
  /** Файл с дашбордами собирается — повторное скачивание не запускается */
  isPdfBuilding: boolean;
  onDownloadPdf: () => void;
  onActivePresetChange: (presetId: string | null) => void;
  onCreatePreset: () => void;
  onEditPreset: (preset: DashboardPreset) => void;
};

/**
 * Шапка страницы дашбордов: название показанного дашборда, список пользовательских дашбордов
 * с действиями над применённым и скачивание файлом.
 *
 * Пока дашборд правится, здесь же стоит и управление режимом — сохранение, отказ от правок и
 * подсказка о несохранённых: у названия правящегося дашборда, как в прочих разделах управления.
 * Отдельной полосы режима над сеткой нет.
 */
export const DashboardPageHeader: FC<Props> = ({
  presetName,
  isEditing,
  draftName,
  onChangeDraftName,
  isDirty,
  canSave,
  onSaveDraft,
  onCancelEditing,
  isPresetsEnabled,
  isFetching,
  isPdfBuilding,
  onDownloadPdf,
  onActivePresetChange,
  onCreatePreset,
  onEditPreset,
}) => {
  const { t } = useTranslation(['common', 'qualityDashboard']);

  return (
    <div className="flex flex-wrap items-center gap-3">
      {isEditing ? (
        <EditableTitle
          value={draftName}
          placeholder={t('qualityDashboard:presets.editor.name')}
          onSave={onChangeDraftName}
          textClassName="text-2xl font-bold"
        />
      ) : (
        <h1 className="text-2xl font-bold">{presetName ?? t('qualityDashboard:title')}</h1>
      )}
      {isFetching && <Spinner className="size-4" />}

      <div className="ml-auto flex flex-wrap items-center gap-2">
        {isEditing && (
          <>
            {/* Подсказка объясняет, чего дашборду не хватает: иначе неактивная кнопка молчит. */}
            {!canSave && (
              <span className="text-sm text-muted-foreground">{t('qualityDashboard:presets.editor.saveHint')}</span>
            )}
            <SaveButton
              isDirty={isDirty && canSave}
              onClick={onSaveDraft}
              secondaryAction={
                <Button variant="ghost" onClick={onCancelEditing}>
                  {t('common:cancel')}
                </Button>
              }
            />
          </>
        )}
        {isPresetsEnabled && !isEditing && (
          <>
            <DashboardPresetsPopover onActivePresetChange={onActivePresetChange} />
            <DashboardPresetActions
              onActivePresetChange={onActivePresetChange}
              onCreatePreset={onCreatePreset}
              onEditPreset={onEditPreset}
            />
          </>
        )}
        <Tooltip tooltip={t('qualityDashboard:download')}>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('qualityDashboard:download')}
            icon={<DownloadIcon className="size-4" />}
            disabled={isPdfBuilding}
            onClick={onDownloadPdf}
          />
        </Tooltip>
      </div>
    </div>
  );
};
